import { useCallback, useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import moment from "moment";
import toast from "react-hot-toast";
import { Landmark, CheckCircle2, AlertCircle, ExternalLink, Clock } from "lucide-react";
import OrganizerSidebar from "../Components/CreateEvent/OrganizerSidebar";
import OrganizerMobileNavDropdown from "../Components/CreateEvent/OrganizerMobileNavDropdown";
import { InstaPassLogo } from "../Components/layout/Components/InstaPassLogo";
import { ApiURL } from "../setting/GlobleVariable";

const headers = () => ({ token: localStorage.getItem("instapass_user_token") || "" });
const money = (cents) =>
  `$${((cents || 0) / 100).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

function errorText(err, fallback) {
  return err?.response?.data?.message || err?.message || fallback;
}

function StatusPill({ row }) {
  const date = (d) => (d ? moment(d).format("MMM D") : "");
  const map = {
    upcoming: ["bg-white/10 text-white/70", "After the event"],
    holding: ["bg-blue-500/15 text-blue-200", `Releases ${date(row.releaseAt)}`],
    scheduled: ["bg-blue-500/15 text-blue-200", "Sending soon"],
    processing: ["bg-blue-500/15 text-blue-200", "Sending"],
    paid: ["bg-emerald-500/15 text-emerald-300", `Paid ${date(row.paidAt)}`],
    awaiting_account: ["bg-amber-400/15 text-amber-200", "Set up payouts to receive"],
    failed: ["bg-red-500/15 text-red-300", "Delayed, retrying"],
    nothing_owed: ["bg-white/10 text-white/50", "Nothing owed"],
    no_date: ["bg-amber-400/15 text-amber-200", "Add an event date"],
  };
  const [cls, label] = map[row.status] || ["bg-white/10 text-white/70", row.status];
  return (
    <span className={`inline-block px-2 py-0.5 rounded-md text-[11px] font-semibold whitespace-nowrap ${cls}`} title={row.failureReason || ""}>
      {label}
    </span>
  );
}

function AccountCard({ account, onSetup, onDashboard, busy }) {
  if (!account) {
    return <div className="rounded-2xl border border-[#1E293B] bg-[#0F1A2E] p-6 text-white/40 text-sm">Checking your payout account…</div>;
  }
  if (account.error) {
    return (
      <div className="rounded-2xl border border-red-500/30 bg-red-500/10 p-6 text-sm text-red-200">
        {account.error}
      </div>
    );
  }
  if (account.payoutsEnabled) {
    return (
      <div className="rounded-2xl border border-emerald-500/30 bg-emerald-500/10 p-6 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div className="flex items-start gap-3">
          <CheckCircle2 className="w-6 h-6 text-emerald-400 shrink-0" />
          <div>
            <p className="text-white font-semibold">Payouts are on</p>
            <p className="text-sm text-white/60 mt-1">Your earnings go to the bank account you added with Stripe.</p>
          </div>
        </div>
        <button
          type="button"
          onClick={onDashboard}
          disabled={busy}
          className="inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-[#1E293B] hover:bg-[#273449] text-white text-sm disabled:opacity-50"
        >
          Bank and tax details <ExternalLink className="w-4 h-4" />
        </button>
      </div>
    );
  }
  return (
    <div className="rounded-2xl border border-amber-400/30 bg-amber-400/10 p-6 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
      <div className="flex items-start gap-3">
        <AlertCircle className="w-6 h-6 text-amber-300 shrink-0" />
        <div>
          <p className="text-white font-semibold">{account.connected ? "Finish setting up payouts" : "Set up payouts to get paid"}</p>
          <p className="text-sm text-white/60 mt-1 max-w-xl">
            {account.connected
              ? "Stripe still needs a few details before it can send your money."
              : "InstaPass uses Stripe to pay organizers. It takes about 5 minutes: your name, bank account and tax details. InstaPass never sees your bank numbers."}
          </p>
        </div>
      </div>
      <button
        type="button"
        onClick={onSetup}
        disabled={busy}
        className="inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-blue-500 hover:bg-blue-600 text-white text-sm font-semibold disabled:opacity-50 shrink-0"
      >
        {busy ? "Opening Stripe…" : account.connected ? "Continue setup" : "Set up payouts"}
      </button>
    </div>
  );
}

/** /organizer/payouts — earnings per event and the Stripe payout account. */
export default function OrganizerPayoutsPage() {
  const [params, setParams] = useSearchParams();
  const [account, setAccount] = useState(null);
  const [summary, setSummary] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    axios
      .get(`${ApiURL}/payouts/account`, { headers: headers() })
      .then(({ data }) => setAccount(data))
      .catch((err) => setAccount({ error: errorText(err, "Couldn't check your payout account") }));
    axios
      .get(`${ApiURL}/payouts/summary`, { headers: headers() })
      .then(({ data }) => setSummary(data))
      .catch((err) => setSummary({ error: errorText(err, "Couldn't load your earnings") }));
  }, []);

  useEffect(() => {
    load();
    const setup = params.get("setup");
    if (setup) {
      if (setup === "done") toast.success("Thanks. Stripe is checking your details.");
      setParams({}, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openStripe = async (path) => {
    setBusy(true);
    try {
      const { data } = await axios.post(`${ApiURL}/payouts/${path}`, {}, { headers: headers() });
      window.location.assign(data.url);
    } catch (err) {
      toast.error(errorText(err, "Couldn't open Stripe"));
      setBusy(false);
    }
  };

  const rows = summary?.events || [];

  return (
    <div className="min-h-screen bg-[#060D1B] text-white" style={{ fontFamily: "'Outfit', 'Inter', sans-serif" }}>
      <OrganizerSidebar />
      <header className="lg:hidden fixed top-0 left-0 right-0 h-14 border-b border-[#1E293B]/60 bg-[#060D1B]/95 backdrop-blur-lg grid grid-cols-[1fr_auto_1fr] items-center gap-2 px-3 z-30">
        <OrganizerMobileNavDropdown className="min-w-0 justify-self-start" />
        <Link to="/" className="justify-self-center shrink-0" aria-label="InstaPass home">
          <InstaPassLogo size="sm" />
        </Link>
        <span />
      </header>

      <main className="lg:pl-[260px] pt-14 lg:pt-0">
        <div className="hidden lg:flex h-[64px] border-b border-[#1E293B]/60 items-center px-6">
          <h1 className="text-[16px] font-bold">Payouts</h1>
        </div>

        <div className="px-4 sm:px-6 py-5 max-w-6xl space-y-5">
          <AccountCard
            account={account}
            busy={busy}
            onSetup={() => openStripe("account/onboard")}
            onDashboard={() => openStripe("account/dashboard")}
          />

          {summary && !summary.error && (
            <p className="flex items-start gap-2 text-sm text-white/60">
              <Clock className="w-4 h-4 mt-0.5 shrink-0" />
              <span>
                You're paid {summary.holdDays} day{summary.holdDays === 1 ? "" : "s"} after each event ends, so refunds and disputes are settled first.
                {summary.feePerTicket > 0
                  ? ` InstaPass keeps $${summary.feePerTicket.toFixed(2)} per ticket, plus card processing (${summary.processing?.percent ?? 2.9}% + ${summary.processing?.fixedCents ?? 30}¢ per order). Buyers pay both at checkout unless you chose to absorb fees for that ticket type.`
                  : ""}{" "}
                Stripe then sends the money to your bank, usually within 2 business days.
              </span>
            </p>
          )}

          {summary && !summary.error && (
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              {[
                ["Coming to you", summary.totals.pendingCents, "text-white"],
                ["Paid out", summary.totals.paidCents, "text-emerald-300"],
                ["Total earned", summary.totals.earnedCents, "text-white"],
              ].map(([label, cents, cls]) => (
                <div key={label} className="rounded-2xl bg-[#0F1A2E] border border-[#1E293B] p-5">
                  <p className="text-[11px] uppercase tracking-wider text-white/40 font-bold">{label}</p>
                  <p className={`text-3xl font-black mt-2 tabular-nums ${cls}`}>{money(cents)}</p>
                </div>
              ))}
            </div>
          )}

          {summary?.error && <p className="text-sm text-red-400">{summary.error}</p>}

          {summary && !summary.error && (
            <ul className="sm:hidden space-y-3">
              {rows.length === 0 && (
                <li className="rounded-2xl border border-[#1E293B] px-4 py-10 text-center text-sm text-white/40">
                  Earnings appear here once your events sell tickets.
                </li>
              )}
              {rows.map((row) => (
                <li key={row.eventId} className="rounded-2xl border border-[#1E293B] bg-[#0F1A2E] p-4">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="text-white font-semibold">{row.name}</p>
                      <p className="text-[11px] text-white/40">
                        {row.endsAt ? `Ends ${moment(row.endsAt).format("MMM D, YYYY")}` : "No event date"}
                      </p>
                    </div>
                    <StatusPill row={row} />
                  </div>
                  <p className="text-2xl font-black text-white mt-3 tabular-nums">{money(row.netCents)}</p>
                  <p className="text-xs text-white/50 mt-1">
                    {row.ticketCount} tickets · {money(row.salesCents)} sales · {money(row.feeCents)} InstaPass fee ·{" "}
                    {money(row.processingCents)} card processing
                  </p>
                  {row.unverifiedOrders ? (
                    <p className="text-[11px] text-amber-200/80 mt-1">
                      {row.unverifiedOrders} older order{row.unverifiedOrders === 1 ? "" : "s"} under review
                    </p>
                  ) : null}
                </li>
              ))}
            </ul>
          )}

          {summary && !summary.error && (
            <div className="hidden sm:block rounded-2xl border border-[#1E293B] overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-[#0F1A2E] text-white/40 text-[11px] uppercase tracking-wider">
                  <tr>
                    <th className="text-left font-semibold px-4 py-3">Event</th>
                    <th className="text-right font-semibold px-4 py-3">Tickets</th>
                    <th className="text-right font-semibold px-4 py-3">Sales</th>
                    <th className="text-right font-semibold px-4 py-3">InstaPass fee</th>
                    <th className="text-right font-semibold px-4 py-3">Card processing</th>
                    <th className="text-right font-semibold px-4 py-3">Your earnings</th>
                    <th className="text-left font-semibold px-4 py-3">Payout</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#1E293B]">
                  {rows.length === 0 && (
                    <tr>
                      <td colSpan={7} className="px-4 py-10 text-center text-white/40">
                        <Landmark className="w-8 h-8 mx-auto mb-2 text-white/20" />
                        Earnings appear here once your events sell tickets.
                      </td>
                    </tr>
                  )}
                  {rows.map((row) => (
                    <tr key={row.eventId} className="text-white/80">
                      <td className="px-4 py-3">
                        <p className="text-white">{row.name}</p>
                        <p className="text-[11px] text-white/40">
                          {row.endsAt ? `Ends ${moment(row.endsAt).format("ddd, MMM D, YYYY")}` : "No event date"}
                          {row.unverifiedOrders ? ` · ${row.unverifiedOrders} older order${row.unverifiedOrders === 1 ? "" : "s"} under review` : ""}
                        </p>
                      </td>
                      <td className="px-4 py-3 text-right tabular-nums">{row.ticketCount}</td>
                      <td className="px-4 py-3 text-right tabular-nums">{money(row.salesCents)}</td>
                      <td className="px-4 py-3 text-right tabular-nums text-white/50">−{money(row.feeCents)}</td>
                      <td className="px-4 py-3 text-right tabular-nums text-white/50">−{money(row.processingCents)}</td>
                      <td className="px-4 py-3 text-right tabular-nums font-semibold text-white">{money(row.netCents)}</td>
                      <td className="px-4 py-3">
                        <StatusPill row={row} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
